
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FileUploadItem } from "./FileUploadItem";
import { Button } from "./Button";
import { DataService } from "../DataService/DataService";
import { VideoDto } from "../Types/VideoTypes";
import { AdminIcon } from "../Icons/AdminIcon";
import { subscribe as joinUploadGroup } from "../DataService/VideoUploadManager";
import './FileUpload.css'
export interface AttachmentDto {
  id: number;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  bunnyUrl: string;
  uploadedAt: string; // ISO date string
}

interface FileWithId {
  file: File;
  uploadId: string;
  // Optional: videoDto when backend has finished processing
  video?: VideoDto | null;
  // local optimistic state
  isUploading?: boolean;
  isConfirming?: boolean;
  error?: string | null;
}

interface FileUploadProps {
  /**
   * Callback invoked when a file has been uploaded and backend returns a VideoDto.
   * Provided arguments: (videoDto, uploadId)
   */
  onVideoUploaded?: (video: VideoDto, uploadId: string) => void;
  onDocumentUploaded?: (attachment: AttachmentDto, uploadId: string) => void;
  accept?: string; // e.g. "video/*"
  resetToken?: number;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onVideoUploaded, onDocumentUploaded,accept = "video/*", resetToken, disabled = false }) => {
  const [files, setFiles] = useState<FileWithId[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [resetToken]);

  const addFilesToState = (newFiles: FileWithId[]) => {
    setFiles(prev => [...prev, ...newFiles]);
  };

  const updateFileEntry = (uploadId: string, patch: Partial<FileWithId>) => {
    setFiles(prev => prev.map(f => (f.uploadId === uploadId ? { ...f, ...patch } : f)));
  };

  const removeFile = (uploadId: string) => {
    setFiles(prev => prev.filter(f => f.uploadId !== uploadId));
  };

  const handleUploadComplete = (uploadId: string, file: File, kind?: string) => {
    toast.success(`${file.name}${kind ? ` (${kind})` : ""} uppladdad`);
    removeFile(uploadId);
  };

  
  const handleFiles = async (newFiles: FileList | null) => {
  if (disabled) return;
  if (!newFiles || newFiles.length === 0) return;

  console.info("[FileUpload] files selected", {
    count: newFiles.length,
    accept,
    disabled
  });

  const filesWithIds: FileWithId[] = Array.from(newFiles).map(file => ({
    file,
    uploadId: crypto.randomUUID(),
    video: null,
    isUploading: false,
    isConfirming: false,
    error: null
  }));

  addFilesToState(filesWithIds);

  filesWithIds.forEach(async ({ file, uploadId }) => {
    try {
      console.info("[FileUpload] upload starting", {
        uploadId,
        fileName: file.name,
        sizeBytes: file.size,
        type: file.type
      });

      await joinUploadGroup(uploadId, () => {});
      updateFileEntry(uploadId, { isUploading: true });

      const res = await DataService.VideoService.UploadAnyFile({
        title: file.name,
        uploadId,
        file,
        description: ""
      });

      if (!res) {
        console.warn("[FileUpload] upload response missing", { uploadId, fileName: file.name });
        updateFileEntry(uploadId, { isUploading: false, isConfirming: false });
        toast.info(`${file.name} laddas upp — väntar på servern...`);
        return;
      }

      console.info("[FileUpload] upload response received", {
        uploadId,
        fileName: file.name,
        type: res.type
      });

      // Enkel och tydlig switch
      if (res.type === "video") {
        const videoDto = res.data;

        updateFileEntry(uploadId, {
          isUploading: false,
          isConfirming: true,
          video: videoDto,
          error: null
        });
        onVideoUploaded?.(videoDto, uploadId);
      }

      else if (res.type === "document") {
        const attachmentDto = res.data;

        updateFileEntry(uploadId, {
          isUploading: false,
          isConfirming: true,
          video: null, // irrelevant
          error: null
        });
        onDocumentUploaded?.(attachmentDto, uploadId);
      }

      else {
        updateFileEntry(uploadId, { isUploading: false, isConfirming: false });
        toast.error(`Okänt svar för ${file.name}`);
      }

    } catch (err: any) {
      console.error("[FileUpload] upload error", { uploadId, fileName: file.name, err });
      updateFileEntry(uploadId, {
        isUploading: false,
        isConfirming: false,
        error: err?.message || "Uppladdningsfel"
      });
      toast.error(`Fel vid uppladdning av ${file.name}`);
    }
  });
};


  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // clear value so same file can be reselected later
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearAll = () => {
    setFiles([]);
  };

  return (
    <div className="uploadContainer">
      <div
        className={`uploadDropzone ${isDragging ? "uploadDropzoneDragging" : "uploadDropzoneDefault"}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => { if (!disabled) fileInputRef.current?.click(); }}
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) fileInputRef.current?.click(); }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="uploadInput"
          accept={accept}
          disabled={disabled}
          style={{ display: "none" }}
        />

        <div className="uploadContent">
          <div className={`uploadIconWrapper ${isDragging ? "uploadIconWrapperDragging" : "uploadIconWrapperDefault"}`}>
            <AdminIcon.FileUpload/>
          </div>

          <h3 className="uploadTitle">
            {isDragging ? "Släpp filerna här" : "Dra och släpp filer här"}
          </h3>
          <p className="uploadSubtitle">eller klicka för att välja filer</p>

          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="submit-button"
            text={"Välj filer"}
            disabled={disabled}
          />
        </div>
      </div>

      {files.length > 0 && (
        <div className="uploadFilesList">
          <div className="uploadFilesHeader">
            <h4 className="uploadFilesHeaderTitle">
              Uppladdningar ({files.length})
            </h4>
            <Button
              type="button"
              text={"Rensa alla"}
              onClick={clearAll}
              className="warning-button"
              disabled={disabled}
            />
          </div>

          <div className="uploadFileItems space-y-3">
            {files.map(({ file, uploadId, isUploading, isConfirming, error }) => (
              <div key={uploadId} className="mb-2">
                <FileUploadItem
                  file={file}
                  uploadId={uploadId}
                  onComplete={handleUploadComplete}
                />
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className={`font-medium ${error ? "text-destructive" : isUploading ? "text-muted-foreground" : "text-success"}`}>
                    {error ? error : isUploading ? "Uppladdning pågår..." : isConfirming ? "Slutförs..." : "Förbereds..."}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    <button onClick={() => removeFile(uploadId)} className="text-sm underline">Ta bort</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
